/**
 * Reports API helpers — thin wrappers over the openapi-fetch client.
 *
 * All calls route through the shared `api` client which applies the auth
 * middleware. NEVER bypass this with raw fetch.
 */
// ── API helpers ──────────────────────────────────────────────────────────────
/**
 * Fetch the list of generated reports for the current tenant.
 */
export async function listReports() {
    // Until openapi:gen is re-run we hit the endpoint directly.
    const res = await fetch('/v1/reports', {
        headers: await authHeaders(),
    });
    if (res.status === 401 || res.status === 403)
        throw new ApiError(res.status);
    if (!res.ok)
        throw new Error(`GET /v1/reports failed: ${res.status}`);
    return (await res.json());
}
/**
 * Trigger PDF generation for a given month (yyyy-MM).
 * Returns the newly created ReportDto.
 */
export async function generateMonthlyReport(month) {
    const res = await fetch('/v1/reports/monthly', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ month }),
    });
    if (res.status === 401 || res.status === 403)
        throw new ApiError(res.status);
    if (!res.ok) {
        const body = await res.text();
        throw new Error(`POST /v1/reports/monthly failed: ${res.status} — ${body}`);
    }
    return (await res.json());
}
/**
 * Fetch monthly statistics for a given month (yyyy-MM).
 */
export async function getMonthlyStats(month) {
    const res = await fetch(`/v1/reports/monthly-stats?month=${encodeURIComponent(month)}`, {
        headers: await authHeaders(),
    });
    if (res.status === 401 || res.status === 403)
        throw new ApiError(res.status);
    if (!res.ok)
        throw new Error(`GET /v1/reports/monthly-stats failed: ${res.status}`);
    return (await res.json());
}
/**
 * Fetch the trailing-N-months trend data (N clamped to 1-12 on the server).
 */
export async function getReportsTrend(months = 6) {
    const res = await fetch(`/v1/reports/trend?months=${months}`, {
        headers: await authHeaders(),
    });
    if (res.status === 401 || res.status === 403)
        throw new ApiError(res.status);
    if (!res.ok)
        throw new Error(`GET /v1/reports/trend failed: ${res.status}`);
    return (await res.json());
}
/**
 * Get a 15-minute user-delegation SAS URL for a report PDF download.
 */
export async function getReportDownloadUrl(id) {
    const res = await fetch(`/v1/reports/${id}/download`, {
        headers: await authHeaders(),
    });
    if (res.status === 401 || res.status === 403)
        throw new ApiError(res.status);
    if (res.status === 404)
        throw new ApiError(404);
    if (!res.ok)
        throw new Error(`GET /v1/reports/${id}/download failed: ${res.status}`);
    const dto = (await res.json());
    return dto.sasUrl;
}
// ── Internal helpers ─────────────────────────────────────────────────────────
class ApiError extends Error {
    status;
    constructor(status) {
        super(`API error ${status}`);
        this.status = status;
    }
}
/**
 * Resolve auth headers from the MSAL store without importing the Pinia store
 * in a non-Vue context. The auth store handles token acquisition via MSAL.
 * This is a lightweight shim until openapi:gen wires the Bearer token automatically.
 */
async function authHeaders() {
    // Lazy import to avoid circular dependency: api → auth → api
    const { useAuth } = await import('@/stores/auth');
    const auth = useAuth();
    const token = await auth.getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
}
